import { Button, Group, Select, Stack } from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';

import { BASE_LABELS, PERIOD_PRESETS, PeriodPreset, REGION_LABELS } from '../../constants/base';
import { analyticsTypes } from '../../types';
import { detectPreset, getPresetRange } from '../../utils/base';

import { endOfDay, startOfDay } from '@/utils/dates';

type ControlsPanelProps = {
  experts: analyticsTypes.BaseExpertListItem[];
  expertId: string | null;
  dateFrom: DateValue;
  dateTo: DateValue;
  isExpertsLoading: boolean;
  onExpertChange: (expertId: string) => void;
  onPeriodChange: (from: Date, to: Date) => void;
};

export const ControlsPanel = ({
  experts,
  expertId,
  dateFrom,
  dateTo,
  isExpertsLoading,
  onExpertChange,
  onPeriodChange,
}: ControlsPanelProps) => {
  const activePreset = detectPreset(dateFrom, dateTo);

  const expertOptions = (['cis', 'la'] as const)
    .map((region) => ({
      group: REGION_LABELS[region],
      items: experts
        .filter((expert) => expert.region === region)
        .map((expert) => ({ value: expert.id, label: expert.name })),
    }))
    .filter((group) => group.items.length > 0);

  const applyPreset = (preset: PeriodPreset) => {
    const [from, to] = getPresetRange(preset);
    onPeriodChange(from, to);
  };

  const changeFrom = (value: DateValue) => {
    if (!value) {
      return;
    }
    const from = startOfDay(value);
    const to = dateTo && dateTo >= from ? dateTo : endOfDay(value);
    onPeriodChange(from, to);
  };

  const changeTo = (value: DateValue) => {
    if (!value) {
      return;
    }
    const to = endOfDay(value);
    const from = dateFrom && dateFrom <= to ? dateFrom : startOfDay(value);
    onPeriodChange(from, to);
  };

  return (
    <Group justify="flex-end" align="flex-end" gap="md" wrap="wrap">
      <Select
        label={BASE_LABELS.EXPERT}
        placeholder={BASE_LABELS.EXPERT_PLACEHOLDER}
        data={expertOptions}
        value={expertId}
        onChange={(value) => value && onExpertChange(value)}
        searchable
        nothingFoundMessage={BASE_LABELS.EXPERT_NOT_FOUND}
        disabled={isExpertsLoading}
        w={320}
      />
      <DatePickerInput
        label={BASE_LABELS.DATE_FROM}
        leftSection={<IconCalendar size={16} />}
        value={dateFrom}
        valueFormat="DD.MM.YYYY"
        onChange={changeFrom}
        maxDate={dateTo || undefined}
        w={150}
      />
      <DatePickerInput
        label={BASE_LABELS.DATE_TO}
        leftSection={<IconCalendar size={16} />}
        value={dateTo}
        valueFormat="DD.MM.YYYY"
        onChange={changeTo}
        minDate={dateFrom || undefined}
        w={150}
      />
      <Stack gap={4}>
        <Group gap={6}>
          {PERIOD_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              size="xs"
              variant={activePreset === preset.value ? 'filled' : 'default'}
              color={activePreset === preset.value ? 'teal' : 'gray'}
              onClick={() => applyPreset(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </Group>
      </Stack>
    </Group>
  );
};
